/**
 * The organization members table — the Organization tab's content. Ported from
 * `bimgo-ai-web`'s `OrgMembersTable`: list members, search, view a member's
 * permissions, and (for `org:manage_roles` holders) edit a member's role.
 * Adapted to 2.5's plain fetch + local `Modal`/`t`.
 *
 * The look follows the web client's: a card headed by an icon and title, a
 * search field with the magnifier inside it, and a plain three-column table —
 * member, role badge, right-aligned ghost actions. The edit dialog is a stack of
 * selectable role cards, each a radio circle beside the role's name and what it
 * grants; deliberately the *name*, not the badge, so the description carries the
 * meaning.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../../lib/config";
import { getOrgMembers, updateMemberRoles, type OrgMember } from "../../lib/members";
import { useLocale } from "../../i18n/LocaleProvider";
import { Modal } from "./Modal";
import { PermissionsDialog } from "./PermissionsDialog";
import { ORG_ROLES, RoleBadge, primaryRole } from "./RoleBadge";
import { LoadingBlock } from "./Spinner";

const ICON = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function UsersIcon() {
  return (
    <svg {...ICON}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg {...ICON} width={14} height={14}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg {...ICON} width={15} height={15}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg {...ICON} width={15} height={15}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg {...ICON} width={15} height={15} style={{ color: "#f59e0b", flex: "none" }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function OrgMembersTable({
  organizationId,
  token,
  canEditRoles,
}: {
  organizationId: string;
  token: string | null;
  canEditRoles: boolean;
}) {
  const { t } = useLocale();

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");

  const [editMember, setEditMember] = useState<OrgMember | null>(null);
  const [editRole, setEditRole] = useState<string>("standard_user");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [viewMember, setViewMember] = useState<OrgMember | null>(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getOrgMembers(tokenRef.current, organizationId, { apiBaseUrl: API_BASE_URL() })
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch(() => {
        if (!cancelled) setError(t("settings.org.members.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, refreshKey, t]);

  const roleLabel = (role: string) => {
    const key = `settings.org.members.role.${role}`;
    const label = t(key);
    return label === key ? role : label;
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.toLowerCase();
    return members.filter((m) => {
      const email = (m.email || m.user_id).toLowerCase();
      if (email.includes(q)) return true;
      return m.roles.some((r) => r !== "system_admin" && roleLabel(r).toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, search]);

  const openEdit = (member: OrgMember) => {
    setSaveError(null);
    setEditMember(member);
    setEditRole(primaryRole(member.roles) ?? "standard_user");
  };

  const save = async () => {
    if (!editMember) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateMemberRoles(tokenRef.current, organizationId, editMember.user_id, [editRole], {
        apiBaseUrl: API_BASE_URL(),
      });
      setEditMember(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const memberLabel = (m: OrgMember) => m.email || `${m.user_id.slice(0, 8)}…`;

  return (
    <div className="settings-card">
      <h3 className="card-title">
        <span className="card-title-icon">
          <UsersIcon />
        </span>
        {t("settings.org.members.title")}
      </h3>

      {!loading && !error && members.length > 0 ? (
        <div className="search-field">
          <span className="search-field-icon">
            <SearchIcon />
          </span>
          <input
            className="settings-input"
            placeholder={t("settings.org.members.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      ) : null}

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <p className="settings-error-row">
          <AlertIcon />
          <span>{error}</span>
        </p>
      ) : filtered.length === 0 ? (
        <p className="settings-note" style={{ textAlign: "center", padding: "1rem 0" }}>
          {t("settings.org.members.empty")}
        </p>
      ) : (
        <div className="members-scroll">
          <table className="members-table">
            <thead>
              <tr>
                <th>{t("settings.org.members.colMember")}</th>
                <th>{t("settings.org.members.colRoles")}</th>
                <th className="col-actions">{t("settings.org.members.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.user_id}>
                  <td className="member-email">{memberLabel(m)}</td>
                  <td>
                    <RoleBadge roles={m.roles} />
                  </td>
                  <td className="col-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      title={t("settings.org.members.view")}
                      aria-label={t("settings.org.members.view")}
                      onClick={() => setViewMember(m)}
                    >
                      <EyeIcon />
                    </button>
                    {canEditRoles ? (
                      <button
                        type="button"
                        className="icon-btn"
                        title={t("settings.org.members.edit")}
                        aria-label={t("settings.org.members.edit")}
                        onClick={() => openEdit(m)}
                      >
                        <PencilIcon />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View permissions */}
      <PermissionsDialog
        open={viewMember !== null}
        onClose={() => setViewMember(null)}
        title={viewMember ? memberLabel(viewMember) : ""}
        actions={viewMember?.actions ?? []}
      />

      {/* Edit role */}
      <Modal
        open={editMember !== null}
        onClose={() => (saving ? undefined : setEditMember(null))}
        title={t("settings.org.members.editDialog.title")}
        description={editMember ? memberLabel(editMember) : undefined}
        footer={
          <>
            <button
              type="button"
              className="settings-btn secondary"
              onClick={() => setEditMember(null)}
              disabled={saving}
            >
              {t("settings.org.members.editDialog.cancel")}
            </button>
            <button type="button" className="settings-btn" onClick={() => void save()} disabled={saving}>
              {saving
                ? t("settings.org.members.editDialog.saving")
                : t("settings.org.members.editDialog.save")}
            </button>
          </>
        }
      >
        <div className="role-options">
          {ORG_ROLES.map((role) => {
            const selected = editRole === role;
            const descKey = `settings.org.members.roleDesc.${role}`;
            const desc = t(descKey);
            return (
              <button
                key={role}
                type="button"
                disabled={saving}
                aria-pressed={selected}
                className={`role-option${selected ? " is-selected" : ""}`}
                onClick={() => setEditRole(role)}
              >
                <span className={`role-radio${selected ? " is-selected" : ""}`}>
                  {selected ? <CheckIcon /> : null}
                </span>
                <span className="role-option-body">
                  <span className="role-option-name">{roleLabel(role)}</span>
                  <span className="role-option-desc">{desc === descKey ? "" : desc}</span>
                </span>
              </button>
            );
          })}
        </div>
        {saveError ? (
          <p className="settings-msg err" role="alert">
            {t("settings.org.members.editDialog.errorTitle")}: {saveError}
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
